import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { FileDown, Mail, Loader2, Package, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ALL_MATERIALS = [
  { key: 'v250', label: 'Vitrine 250 cm' }, { key: 'v196', label: 'Vitrine 196 cm' },
  { key: 'v146', label: 'Vitrine 146 cm' }, { key: 'v116', label: 'Vitrine 116 cm' },
  { key: 'bac120', label: 'Bac réfrigéré 120 x 120' }, { key: 'wok', label: 'Wok' },
  { key: 'rice', label: 'Machine à tapisser le riz' },
];
const PERIOD_TYPES = [
  { v: 'all', l: 'Toutes les dates' }, { v: 'month', l: 'Mois' }, { v: 'quarter', l: 'Trimestre' },
  { v: 'semester', l: 'Semestre' }, { v: 'year', l: 'Année' },
];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const periodLabel = (p) => {
  if (/^\d{4}-\d{2}$/.test(p)) return `${MONTHS[Number(p.slice(5)) - 1]} ${p.slice(0, 4)}`;
  const m = p.match(/^(\d{4})-([TS]\d)$/);
  return m ? `${m[2]} ${m[1]}` : p;
};
const fmtDate = (d) => (d && d.length === 10 ? `${d.slice(8)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : 'Non renseignée');

const MaterialChips = ({ selected, onToggle }) => (
  <div className="flex flex-wrap gap-2" data-testid="recap-material-filters">
    {ALL_MATERIALS.map(m => {
      const on = selected.includes(m.key);
      return (
        <button key={m.key} type="button" onClick={() => onToggle(m.key)} data-testid={`recap-mat-${m.key}`}
          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${on ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-orange-50'}`}>
          {m.label}
        </button>
      );
    })}
  </div>
);

const EmailDialog = ({ open, onClose, users, onSend, sending }) => {
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => { if (open) { setPicked([]); setQ(''); } }, [open]);
  const list = users.filter(u => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="recap-email-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-orange-500" />Envoyer l'export par mail</DialogTitle></DialogHeader>
        <Input placeholder="Rechercher un utilisateur…" value={q} onChange={e => setQ(e.target.value)} data-testid="recap-email-search" />
        <div className="max-h-72 overflow-y-auto space-y-1 border rounded-lg p-2">
          {list.map(u => (
            <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`recap-email-user-${u.id}`}>
              <input type="checkbox" checked={picked.includes(u.id)} onChange={() => toggle(u.id)} className="accent-orange-500" />
              <span className="font-medium text-slate-800">{u.name}</span><span className="text-xs text-slate-500 truncate">{u.email}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white gap-1" disabled={!picked.length || sending}
            onClick={() => onSend(picked)} data-testid="recap-email-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Envoyer ({picked.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RecapTable = ({ data }) => (
  <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
    <table className="w-full text-sm" data-testid="recap-table">
      <thead className="bg-slate-50 text-xs text-slate-500">
        <tr>
          <th className="text-left p-3">Ouverture</th><th className="text-left p-3">Projet</th><th className="text-left p-3">Origine</th>
          {data.materials.map(m => <th key={m.key} className="p-3 text-center">{m.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {data.rows.length === 0 ? (
          <tr><td colSpan={3 + data.materials.length} className="p-6 text-center text-slate-400">Aucun dossier signé sur cette sélection</td></tr>
        ) : data.rows.map(r => (
          <tr key={r.simulation_id} className="border-t" data-testid={`recap-row-${r.simulation_id}`}>
            <td className="p-3 whitespace-nowrap">{fmtDate(r.opening_date)}</td>
            <td className="p-3"><p className="font-medium text-slate-800">{r.project_name || '—'}</p><p className="text-xs text-slate-500">{[r.retailer_name, r.city].filter(Boolean).join(' · ')}</p></td>
            <td className="p-3"><div className="flex flex-wrap gap-1">{r.sources.map(s => <Badge key={s} variant="outline" className={s === 'Signé' ? 'border-emerald-200 text-emerald-700' : 'border-indigo-200 text-indigo-700'}>{s}</Badge>)}</div></td>
            {data.materials.map(m => <td key={m.key} className={`p-3 text-center tabular-nums ${r.quantities[m.key] ? 'font-bold text-slate-800' : 'text-slate-300'}`}>{r.quantities[m.key] || '—'}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </CardContent></Card>
);

export const MaterielRecapTab = ({ token, users }) => {
  const [materials, setMaterials] = useState([]);
  const [periodType, setPeriodType] = useState('all');
  const [periodValue, setPeriodValue] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const auth = { Authorization: `Bearer ${token}` };

  const params = useCallback(() => {
    const p = new URLSearchParams({ period_type: periodType });
    if (periodType !== 'all' && periodValue) p.set('period_value', periodValue);
    if (materials.length) p.set('materials', materials.join(','));
    return p;
  }, [periodType, periodValue, materials]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/materiel-recap?${params()}`, { headers: auth })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setData).catch(() => toast.error('Erreur de chargement du récapitulatif'))
      .finally(() => setLoading(false));
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMat = (k) => setMaterials(p => (p.includes(k) ? p.filter(x => x !== k) : [...p, k]));
  const changeType = (v) => { setPeriodType(v); setPeriodValue(''); };

  const exportXlsx = async () => {
    const res = await fetch(`${API}/api/materiel-recap/export?${params()}`, { headers: auth });
    if (!res.ok) { toast.error("Erreur lors de l'export"); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `recap_materiel_${periodValue || 'toutes_periodes'}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const sendEmail = async (userIds) => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/materiel-recap/email`, {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: userIds, period_type: periodType, period_value: periodType !== 'all' ? periodValue || null : null, materials: materials.join(',') || null }),
      });
      const d = await res.json();
      if (res.ok) { toast.success(`Export envoyé à ${d.sent.length} destinataire(s)`); setShowEmail(false); }
      else toast.error(d.detail || "Erreur d'envoi");
    } catch { toast.error('Erreur réseau'); } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="materiel-recap-tab">
      <Card className="border-slate-200"><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Filter className="w-4 h-4 text-orange-500" />Nature du matériel</div>
        <MaterialChips selected={materials} onToggle={toggleMat} />
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-sm font-semibold text-slate-700">Période d'ouverture</span>
          <select value={periodType} onChange={e => changeType(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="recap-period-type">
            {PERIOD_TYPES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
          {periodType !== 'all' && (
            <select value={periodValue} onChange={e => setPeriodValue(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="recap-period-value">
              <option value="">Toutes</option>
              {(data?.periods || []).map(p => <option key={p} value={p}>{periodLabel(p)}</option>)}
            </select>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" className="gap-1" onClick={exportXlsx} data-testid="recap-export-btn"><FileDown className="w-4 h-4" />Export Excel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white gap-1" onClick={() => setShowEmail(true)} data-testid="recap-email-btn"><Mail className="w-4 h-4" />Envoyer par mail</Button>
          </div>
        </div>
      </CardContent></Card>

      {loading || !data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="recap-totals">
            {data.materials.map(m => (
              <Card key={m.key} className="border-orange-200"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-orange-600 tabular-nums" data-testid={`recap-total-${m.key}`}>{data.totals[m.key]}</p>
                <p className="text-xs text-slate-500">{m.label}</p>
              </CardContent></Card>
            ))}
          </div>
          <p className="text-sm text-slate-600 flex items-center gap-1.5 pt-1" data-testid="recap-count"><Package className="w-4 h-4 text-orange-500" />{data.rows.length} dossier(s) signé(s) ou annoncé(s) dans ALL</p>
          <RecapTable data={data} />
        </>
      )}
      <EmailDialog open={showEmail} onClose={() => setShowEmail(false)} users={users} onSend={sendEmail} sending={sending} />
    </div>
  );
};
